import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@easydent/shared-ui';
import {
  generateConsultationSummary,
  synthesizeSpeech,
  transcribeDictation,
  type DictationResult,
} from '@/lib/api/consultations';
import type { NurseWakeWord } from '@/lib/wakeWord';

interface Props {
  consultationId: string;
  /** Read-only consultation: briefing still works, but dictation (which edits fields) is off. */
  disabled?: boolean;
  onDictation?: (r: DictationResult) => void;
}

const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
const voiceSupported = typeof window !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined' && 'indexedDB' in window;

type VoiceState = 'off' | 'starting' | 'needsTeach' | 'teaching' | 'listening' | 'recording' | 'processing';

const WORD_KEY = 'easydent-nurse-word';

/**
 * The AI nurse: one always-available floating assistant in the corner of the consultation.
 * Combines the three ways to work with her — Brief me (spoken in a natural AI voice),
 * push-to-talk Dictation, and hands-free voice ("say your word, then talk"). Replaces the
 * two separate panels that used to sit above the chart.
 */
const NursePanel: React.FC<Props> = ({ consultationId, disabled, onDictation }) => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language?.startsWith('ro') ? 'ro-RO' : 'en-US';

  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState(false);

  // Push-to-talk dictation.
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Hands-free wake word.
  const [voice, setVoice] = useState<VoiceState>('off');
  const [teachPrompt, setTeachPrompt] = useState<{ say: boolean; i: number; total: number } | null>(null);
  const [word, setWord] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem(WORD_KEY)) || 'nurse');
  const wwRef = useRef<NurseWakeWord | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => {
    if (ttsSupported) window.speechSynthesis.cancel();
    audioRef.current?.pause();
    if (timerRef.current) clearInterval(timerRef.current);
    wwRef.current?.stopListening();
  }, []);

  // --- Read-aloud: natural AI voice, with the browser voice as a fallback ---
  const fallbackSpeak = (text: string) => {
    if (!ttsSupported || !text) { setSpeaking(false); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  };

  const speak = async (text: string) => {
    if (!text) return;
    stopSpeak();
    setSpeaking(true);
    try {
      const blob = await synthesizeSpeech(text);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { URL.revokeObjectURL(url); fallbackSpeak(text); };
      await audio.play();
    } catch {
      // AI voice unavailable (offline/unconfigured) — fall back to the browser voice.
      fallbackSpeak(text);
    }
  };

  const stopSpeak = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (ttsSupported) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const brief = async () => {
    setLoading(true); setError(null);
    try {
      const s = await generateConsultationSummary(consultationId);
      setSummary(s);
      await speak(s);
    } catch {
      setError(t('aiNurse.error', 'Could not generate the briefing. Check that AI is configured and the wallet balance.'));
    } finally { setLoading(false); }
  };

  // --- Push-to-talk dictation ---
  const startRec = async () => {
    setError(null);
    if (!voiceSupported) {
      setError(t('dictation.unsupported', 'Audio recording is not supported in this browser.'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        setBusy(true);
        try {
          onDictation?.(await transcribeDictation(consultationId, blob, duration));
        } catch {
          setError(t('dictation.failed', 'Transcription failed. Check the wallet balance and that AI is configured.'));
        } finally { setBusy(false); }
      };
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setElapsed(0);
      recorder.start();
      setRecording(true);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch {
      setError(t('dictation.micDenied', 'Microphone access was denied.'));
    }
  };

  const stopRec = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    recorderRef.current?.stop();
  };

  // --- Hands-free: wake word → dictation ---
  const onWake = async () => {
    const ww = wwRef.current;
    if (!ww || disabled) return;
    setVoice('recording');
    await ww.stopListening();
    try {
      const { recordUntilSilence } = await import('@/lib/voiceRecorder');
      const cap = await recordUntilSilence();
      if (cap.spoke && cap.speechMs >= 800) {
        setVoice('processing');
        const result = await transcribeDictation(consultationId, cap.blob, cap.durationSeconds);
        onDictation?.(result);
      }
    } catch {
      setError(t('dictation.failed', 'Transcription failed. Check the wallet balance and that AI is configured.'));
    } finally {
      if (wwRef.current) { await wwRef.current.startListening(onWake); setVoice('listening'); }
      else setVoice('off');
    }
  };

  const enableVoice = async () => {
    setError(null); setVoice('starting');
    try {
      const { NurseWakeWord } = await import('@/lib/wakeWord');
      const ww = new NurseWakeWord();
      wwRef.current = ww;
      const trained = await ww.init();
      if (trained) { await ww.startListening(onWake); setVoice('listening'); }
      else setVoice('needsTeach');
    } catch {
      setError(t('aiNurse.voiceError', 'Could not start the voice nurse in this browser.'));
      setVoice('off');
    }
  };

  const teach = async () => {
    const ww = wwRef.current;
    if (!ww) return;
    const name = word.trim() || 'nurse';
    setVoice('teaching');
    try {
      await ww.teach(name, 12, (label, i, total) => setTeachPrompt({ say: label === 'word', i, total }));
      localStorage.setItem(WORD_KEY, name);
      setWord(name);
      setTeachPrompt(null);
      await ww.startListening(onWake);
      setVoice('listening');
    } catch {
      setError(t('aiNurse.teachError', 'Training did not complete. Try again in a quieter spot.'));
      setVoice('needsTeach');
    }
  };

  const rename = async () => {
    const { clearTraining } = await import('@/lib/wakeWord');
    await wwRef.current?.stopListening();
    await clearTraining();
    setVoice('needsTeach');
  };

  const disableVoice = async () => { await wwRef.current?.stopListening(); wwRef.current = null; setVoice('off'); };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;
  const statusText =
    recording ? mmss
    : busy || voice === 'processing' ? t('dictation.transcribing', 'Transcribing…')
    : speaking ? t('aiNurse.speaking', 'Speaking…')
    : voice === 'recording' ? t('aiNurse.recording', 'Recording…')
    : voice === 'listening' ? t('aiNurse.listening', 'Listening for “{{word}}”…', { word })
    : null;

  return (
    <div className="border-b border-line-soft bg-brand-50/30 px-4 py-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs text-white" aria-hidden>✦</span>
            <span className="text-sm font-medium text-body">{t('aiNurse.title', 'AI nurse')}</span>
            {statusText && (
              <span className="ml-auto flex items-center gap-1.5 text-xs text-brand-ink-strong">
                <span className={`inline-block h-2 w-2 rounded-full ${recording || voice === 'recording' ? 'bg-red-600' : 'bg-brand-600'} animate-pulse`} />
                {statusText}
              </span>
            )}
          </div>
          <p className="mb-3 text-xs text-muted">
            {t('aiNurse.hint', 'Say “{{word}}”, then talk — she briefs, writes notes, and proposes interventions and chart edits.', { word })}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" loading={loading} onClick={brief}>
              {loading ? t('aiNurse.briefing', 'Briefing…') : t('aiNurse.brief', 'Brief me on this patient')}
            </Button>
            {summary && (
              speaking
                ? <Button size="sm" variant="secondary" onClick={stopSpeak}>{t('aiNurse.stop', 'Stop')}</Button>
                : <Button size="sm" variant="secondary" onClick={() => speak(summary)}>{t('aiNurse.readAloud', 'Read aloud')}</Button>
            )}
          </div>

          {!disabled && voiceSupported && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {!recording ? (
                <Button size="sm" variant="secondary" onClick={startRec} loading={busy} disabled={voice === 'recording'}>
                  {t('dictation.record', 'Record')}
                </Button>
              ) : (
                <Button size="sm" variant="danger" onClick={stopRec}>{t('dictation.stop', 'Stop')}</Button>
              )}
              {voice === 'off'
                ? <Button size="sm" variant="ghost" onClick={enableVoice}>{t('aiNurse.enableVoice', 'Voice: off')}</Button>
                : <Button size="sm" variant="ghost" onClick={disableVoice}>{t('aiNurse.disableVoice', 'Voice: on')}</Button>}
              {voice === 'listening' && (
                <button onClick={rename} className="text-xs text-brand-ink hover:underline">{t('aiNurse.rename', 'Rename')}</button>
              )}
            </div>
          )}

          {voice === 'starting' && <p className="mt-2 text-xs text-muted">{t('aiNurse.loadingVoice', 'Starting the voice nurse…')}</p>}

          {voice === 'needsTeach' && (
            <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50/60 p-3">
              <p className="mb-2 text-sm text-body-soft">{t('aiNurse.teachIntro', 'Give your nurse a name, then say it a few times when prompted (one-time, stays on this device).')}</p>
              <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('aiNurse.namePrompt', 'Wake word')}</label>
              <input
                value={word} onChange={(e) => setWord(e.target.value)} maxLength={20}
                placeholder={t('aiNurse.namePlaceholder', 'e.g. nurse, Maria, Ana…')}
                className="mb-3 w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <Button size="sm" disabled={!word.trim()} onClick={teach}>{t('aiNurse.teachStart', 'Teach “{{word}}”', { word: word.trim() || '…' })}</Button>
            </div>
          )}
          {voice === 'teaching' && teachPrompt && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <p className="text-sm font-medium text-amber-800">
                {teachPrompt.say ? t('aiNurse.sayWord', 'Say: “{{word}}”', { word }) : t('aiNurse.stayQuiet', 'Stay quiet…')}
              </p>
              <p className="mt-1 text-xs text-amber-700">{teachPrompt.i + 1} / {teachPrompt.total}</p>
            </div>
          )}

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {summary && (
            <p className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg bg-brand-50/60 p-3 text-sm text-body-soft">{summary}</p>
          )}
    </div>
  );
};

export default NursePanel;
